import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Eye, ThumbsUp, MessageSquare, Send, MapPin, Briefcase, Sparkles, MessageCircle, User, X, Lock, Mail } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { MembershipBadge, DocumentVerifiedBadge } from "../components/MembershipBadge";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import MembershipGuardModal, { canSendDM } from "../components/MembershipGuardModal";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GENDER_LABELS = { male: "남성", female: "여성" };
const EMPTY = "미입력";
const CATEGORY_NAMES = {
  notice: "공지", welcome: "가입인사", love: "연애와 썸", breakup: "이별&상처",
  daily: "일상&취미", advice: "고민상담", self: "자기관리&매력", friends: "친구&모임",
  life: "인생2막 노후계획", career: "직장&커리어", health: "건강&뷰티",
  global: "글로벌 싱글라이프", date: "데이트 코스추천", review: "가입후기", success: "커플 성공 스토리",
};

export const AuthorPostsPage = () => {
  const { authorId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, token } = useAuth();
  const [posts, setPosts] = useState([]);
  const [author, setAuthor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageContent, setMessageContent] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [messageSent, setMessageSent] = useState(false);
  const [dmGuard, setDmGuard] = useState(null);
  const dmAllowed = canSendDM(user);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [postsRes, profileRes] = await Promise.all([
          fetch(`${API_URL}/api/posts/by-author/${authorId}`),
          fetch(`${API_URL}/api/users/${authorId}/public-profile`),
        ]);
        if (postsRes.ok) {
          const data = await postsRes.json();
          setPosts(data.posts || []);
        }
        if (profileRes.ok) setAuthor(await profileRes.json());
      } catch { /* ignore */ }
      setLoading(false);
    };
    fetchData();
  }, [authorId]);

  const handleSendMessage = () => {
    if (!isAuthenticated) {
      setDmGuard("auth");
      return;
    }
    if (user?.id === authorId) return; // 자신에게 메시지 불가
    if (!dmAllowed) {
      setDmGuard("dm_upgrade");
      return;
    }
    setMessageSent(false);
    setMessageContent("");
    setShowMessageModal(true);
  };

  const handleSubmitMessage = async () => {
    if (!author || !token || !messageContent.trim()) return;
    setSendingMessage(true);
    try {
      const res = await fetch(`${API_URL}/api/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ receiver_id: authorId, content: messageContent.trim() }),
      });
      if (res.ok) {
        setMessageSent(true);
        setShowMessageModal(false);
        setMessageContent("");
      } else {
        try {
          const data = await res.clone().json();
          alert(data.detail || "메시지 보내기에 실패했습니다.");
        } catch {
          alert("메시지 보내기에 실패했습니다.");
        }
      }
    } catch {
      alert("메시지 보내기에 실패했습니다.");
    }
    setSendingMessage(false);
  };

  const getRelativeTime = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - date) / 60000);
    if (diff < 1) return "방금 전";
    if (diff < 60) return `${diff}분 전`;
    if (diff < 1440) return `${Math.floor(diff / 60)}시간 전`;
    if (diff < 10080) return `${Math.floor(diff / 1440)}일 전`;
    return date.toLocaleDateString("ko-KR");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] pt-20 flex items-center justify-center">
        <div className="text-stone-400">불러오는 중...</div>
      </div>
    );
  }

  const avatarColor = author?.membership_type === "vip" ? "bg-purple-400" :
    author?.membership_type === "premium" ? "bg-amber-400" :
    author?.membership_type === "regular" ? "bg-emerald-400" : "bg-slate-400";

  const genderAge = [GENDER_LABELS[author?.gender], author?.age ? `${author.age}세` : ""].filter(Boolean).join(", ") || EMPTY;
  const location = [author?.country, author?.city].filter(Boolean).join(" ") || EMPTY;
  const job = [author?.job, author?.company_name].filter(Boolean).join(" / ") || EMPTY;

  return (
    <div className="min-h-screen bg-[#FAFAF9] pt-20" data-testid="author-posts-page">
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-stone-500 hover:text-stone-700 mb-6 transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">뒤로가기</span>
        </button>

        <div className="flex gap-8">
          {/* Left sidebar - Fixed profile */}
          {author && (
            <aside className="w-72 flex-shrink-0">
              <div className="sticky top-24 bg-white rounded-2xl shadow-sm border border-stone-100 overflow-hidden" data-testid="author-sidebar">
                {/* Profile header */}
                <div className={`${avatarColor} p-6 flex flex-col items-center`}>
                  <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center border-3 border-white/40 mb-3">
                    <span className="text-white font-bold text-3xl">{author.username?.[0] || "?"}</span>
                  </div>
                  <h2 className="text-white font-bold text-lg">{author.username}</h2>
                  <div className="mt-1 flex items-center gap-1">
                    <MembershipBadge membership={author.membership_type} size="sm" />
                    <DocumentVerifiedBadge verified={author.document_verified} size="sm" />
                  </div>
                  <p className="text-white/70 text-xs mt-2">작성한 글 {posts.length}개</p>
                </div>

                {/* Profile info */}
                <div className="p-5 space-y-3">
                  <div className="flex items-center gap-2.5 text-sm">
                    <User className="w-4 h-4 text-stone-400 flex-shrink-0" />
                    <span className={genderAge !== EMPTY ? "text-stone-700" : "text-stone-300"}>{genderAge}</span>
                  </div>
                  <div className="flex items-center gap-2.5 text-sm">
                    <MapPin className="w-4 h-4 text-stone-400 flex-shrink-0" />
                    <span className={location !== EMPTY ? "text-stone-700" : "text-stone-300"}>{location}</span>
                  </div>
                  <div className="flex items-center gap-2.5 text-sm">
                    <Briefcase className="w-4 h-4 text-stone-400 flex-shrink-0" />
                    <span className={job !== EMPTY ? "text-stone-700" : "text-stone-300"}>{job}</span>
                  </div>
                  <div className="flex items-start gap-2.5 text-sm">
                    <Sparkles className="w-4 h-4 text-stone-400 flex-shrink-0 mt-0.5" />
                    {author.personality && author.personality.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {author.personality.map((p, i) => (
                          <span key={i} className="px-2 py-0.5 bg-stone-100 text-stone-600 rounded-full text-xs">{p}</span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-stone-300">{EMPTY}</span>
                    )}
                  </div>
                  {author.introduction && (
                    <div className="pt-3 border-t border-stone-100">
                      <div className="flex items-start gap-2.5 text-sm">
                        <MessageCircle className="w-4 h-4 text-stone-400 flex-shrink-0 mt-0.5" />
                        <p className="text-stone-600 text-xs leading-relaxed line-clamp-4">{author.introduction}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Send message button */}
                <div className="px-5 pb-5">
                  <button
                    onClick={handleSendMessage}
                    disabled={messageSent || user?.id === authorId}
                    className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                      messageSent 
                        ? "bg-green-600 text-white cursor-default"
                        : user?.id === authorId
                        ? "bg-stone-200 text-stone-400 cursor-not-allowed"
                        : dmAllowed
                          ? "bg-burgundy-600 hover:bg-burgundy-700 text-white"
                          : "bg-stone-100 text-stone-600 hover:bg-stone-200 border border-stone-200"
                    }`}
                    data-testid="send-message-btn"
                  >
                    {messageSent ? <MessageCircle className="w-4 h-4" /> : dmAllowed ? <Send className="w-4 h-4" /> : <Lock className="w-3.5 h-3.5" />}
                    {messageSent ? "메시지 전송됨" : "메시지 보내기"}
                    {!dmAllowed && isAuthenticated && !messageSent && user?.id !== authorId && (
                      <span className="text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded ml-1">정회원</span>
                    )}
                  </button>
                </div>
              </div>
            </aside>
          )}

          {/* Right - Posts list */}
          <main className="flex-1 min-w-0">
            <h2 className="font-bold text-stone-800 mb-4">{author?.username}님의 글 ({posts.length})</h2>
            {posts.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-12 text-center">
                <p className="text-stone-400">작성한 글이 없습니다.</p>
              </div>
            ) : (
              <div className="space-y-2" data-testid="author-posts-list">
                {posts.map((post) => (
                  <article
                    key={post.id}
                    onClick={() => navigate(`/community/post/${post.id}`)}
                    className="bg-white rounded-xl shadow-sm border border-stone-100 px-5 py-4 hover:shadow-md transition-shadow cursor-pointer"
                    data-testid={`author-post-${post.id}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-stone-100 text-stone-500">
                        {CATEGORY_NAMES[post.category] || post.categoryName || post.category}
                      </span>
                      <span className="text-xs text-stone-400">
                        {getRelativeTime(post.created_at || post.date)}
                      </span>
                    </div>
                    <h3 className="font-semibold text-stone-800 mb-1.5 line-clamp-1">{post.title}</h3>
                    <p className="text-sm text-stone-500 line-clamp-2 mb-3">{post.content}</p>
                    <div className="flex items-center gap-4 text-xs text-stone-400">
                      <span className="flex items-center gap-1"><Eye className="w-3.5 h-3.5" />{post.views || 0}</span>
                      <span className="flex items-center gap-1"><ThumbsUp className="w-3.5 h-3.5" />{post.likes || 0}</span>
                      <span className="flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" />{post.comments || 0}</span>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </main>
        </div>
      </div>

      {/* 메시지 보내기 모달 */}
      <AnimatePresence>
        {showMessageModal && author && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            onClick={() => { setShowMessageModal(false); setMessageContent(""); }}
            data-testid="message-modal"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-5 border-b border-stone-100 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-stone-500" />
                  <h3 className="font-bold text-stone-800">메시지 보내기</h3>
                </div>
                <button onClick={() => { setShowMessageModal(false); setMessageContent(""); }} className="text-stone-400 hover:text-stone-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-5">
                <div className="flex items-center gap-3 mb-4 p-3 bg-stone-50 rounded-lg">
                  <div className={`w-10 h-10 rounded-full ${avatarColor} flex items-center justify-center text-sm font-semibold text-white`}>
                    {author.username?.[0] || "?"}
                  </div>
                  <div>
                    <p className="font-medium text-stone-700">{author.username}</p>
                    <p className="text-xs text-stone-400">에게 메시지 보내기</p>
                  </div>
                </div>
                <textarea
                  value={messageContent}
                  onChange={(e) => setMessageContent(e.target.value)}
                  placeholder="메시지 내용을 입력하세요..."
                  className="w-full h-32 p-3 border border-stone-200 rounded-xl text-sm text-stone-700 resize-none focus:outline-none focus:ring-2 focus:ring-stone-300 focus:border-transparent"
                  data-testid="message-textarea"
                  maxLength={500}
                />
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs text-stone-400">{messageContent.length}/500</span>
                  <span className="text-xs text-stone-400">일반회원 하루 1건</span>
                </div>
              </div>
              <div className="p-5 pt-0 flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => { setShowMessageModal(false); setMessageContent(""); }}>취소</Button>
                <Button
                  className="flex-1 bg-gradient-to-r from-burgundy-500 to-burgundy-700 hover:from-burgundy-600 hover:to-burgundy-800 text-white shadow-md hover:shadow-lg transition-all"
                  onClick={handleSubmitMessage}
                  disabled={sendingMessage || !messageContent.trim()}
                  data-testid="send-message-submit"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {sendingMessage ? "전송 중..." : "보내기"}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <MembershipGuardModal
        open={!!dmGuard}
        reason={dmGuard || "dm_upgrade"}
        onClose={() => setDmGuard(null)}
      />
    </div>
  );
};

export default AuthorPostsPage;
